import { MAX_CERTIFICATE_PAGE_BYTES } from '@felix/domain';
import {
  ActionIcon,
  Alert,
  Button,
  Drawer,
  Modal,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconFileTypePdf,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import { useUnsavedChanges } from '../../shell-context.js';
import { formatFileSize } from '../documents/library.js';
import classes from './Compliance.module.css';
import { useCertificateErrorText } from './errors.js';
import { SCAN_ACCEPT, useErrorOf, useSchemaForm } from './form-parts.js';
import { certificateFormSchema } from './forms.js';
import type {
  CertificateDetail,
  CertificateInput,
  CertificateListItem,
  CertificatePage,
  CertificateType,
  OnBoardIssuance,
} from './types.js';

export interface CertificateValues {
  typeId: string | null;
  number: string;
  name: string;
  issuedBy: string;
  issueDate: string | null;
  expiryDate: string | null;
  anniversaryDate: string | null;
  nextSurveyOverride: string | null;
  windowFromOverride: string | null;
  windowToOverride: string | null;
  overrideReason: string;
  renewedFromId: string | null;
  onBoardIssuanceId: string | null;
  [key: string]: unknown;
}

/** Formun başlangıcı: yeni sertifikada ağaçta seçili tür; düzenlemede kaydın alanları. */
export function certificateValues(
  detail: CertificateDetail | null,
  typeId: string | null = null,
): CertificateValues {
  return {
    typeId: detail?.type?.id ?? typeId,
    number: detail?.number ?? '',
    name: detail?.name ?? '',
    issuedBy: detail?.issuedBy ?? '',
    issueDate: detail?.issueDate ?? null,
    expiryDate: detail?.expiryDate ?? null,
    anniversaryDate: detail?.anniversaryDate ?? null,
    nextSurveyOverride: detail?.nextSurveyOverride ?? null,
    windowFromOverride: detail?.windowFromOverride ?? null,
    windowToOverride: detail?.windowToOverride ?? null,
    overrideReason: detail?.overrideReason ?? '',
    renewedFromId: detail?.renewedFromId ?? null,
    onBoardIssuanceId: detail?.onBoardIssuanceId ?? null,
  };
}

/** Sayfa dosyası ekranda da denetlenir (sunucu yeniden denetler; ADR 0029 D12). */
export function pageFileProblem(file: Pick<File, 'size' | 'type' | 'name'>): string | null {
  if (file.size === 0) return 'EMPTY_FILE';
  if (file.size > MAX_CERTIFICATE_PAGE_BYTES) return 'FILE_TOO_LARGE';
  const okType = ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type);
  const okName = /\.(pdf|jpe?g|png)$/i.test(file.name);
  return okType || okName ? null : 'UNSUPPORTED_FILE_TYPE';
}

export interface PageActions {
  upload: (
    file: File,
    onProgress: (progress: { sent: number; total: number }) => void,
  ) => Promise<CertificatePage>;
  move: (page: CertificatePage, sortOrder: number) => Promise<void>;
  remove: (page: CertificatePage) => Promise<void>;
}

export interface CertificateDrawerProps {
  opened: boolean;
  /** `null`: yeni sertifika. */
  detail: CertificateDetail | null;
  types: readonly CertificateType[];
  /** Yenileme bağının adayları (bu geminin ofis sertifikaları). */
  items: readonly CertificateListItem[];
  /** Bağlanabilecek gemide düzenlenen sertifikalar (ofis kaydı gelmemiş). */
  issuances: readonly OnBoardIssuance[];
  typeId: string | null;
  today: string;
  canRemove: boolean;
  onClose: () => void;
  onSave: (
    input: CertificateInput,
    current: CertificateDetail | null,
  ) => Promise<CertificateDetail>;
  onRemove: (detail: CertificateDetail) => Promise<void>;
  pages: (certificateId: string) => PageActions;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className={classes.fieldset}>
      <legend>{title}</legend>
      <Stack gap="sm">{children}</Stack>
    </fieldset>
  );
}

/**
 * Add / Edit Certificate (ofis; plan M7 Task 27b; K28, K29): tür, no, ad, düzenleyen, tarihler,
 * yıl dönümü, hesaplanan pencerenin gerekçeli üstüne yazılması, yenileme bağı ve gemide düzenlenen
 * kayda bağlama; kayıttan sonra taranmış sayfalar tus ile yüklenir ve sıralanır (ADR 0029 D12).
 */
export function CertificateDrawer({
  opened,
  detail,
  types,
  items,
  issuances,
  typeId,
  today,
  canRemove,
  onClose,
  onSave,
  onRemove,
  pages: pageActions,
}: CertificateDrawerProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.dialogs.certificate.${key}`, values);
  const errorText = useCertificateErrorText();
  const errorOf = useErrorOf();
  const schema = certificateFormSchema(today);
  const form = useSchemaForm<CertificateValues>(schema, certificateValues(detail, typeId));
  const [current, setCurrent] = useState<CertificateDetail | null>(detail);
  const [pages, setPages] = useState<CertificatePage[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [upload, setUpload] = useState<{ name: string; percent: number } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);

  // biome-ignore lint/correctness/useExhaustiveDependencies: açılışta ve kayıt değişince sıfırlanır
  useEffect(() => {
    if (!opened) return;
    setCurrent(detail);
    setPages([...(detail?.pages ?? [])].sort((a, b) => a.sortOrder - b.sortOrder));
    form.reset(certificateValues(detail, typeId));
    setError(null);
    setSaved(false);
    setUpload(null);
  }, [opened, detail?.id]);

  const sortedTypes = [...types].sort((a, b) => a.sortOrder - b.sortOrder);
  const renewals = items.filter(
    (item) => item.source === 'office' && item.id !== current?.id && !item.officeEntryPending,
  );
  const linkable = issuances.filter(
    (i) => i.linkedCertificateId === null || i.linkedCertificateId === current?.id,
  );

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const input = schema.parse(values) as CertificateInput;
      const result = await onSave(input, current);
      setCurrent(result);
      setPages([...result.pages].sort((a, b) => a.sortOrder - b.sortOrder));
      form.reset(certificateValues(result));
      if (!current) setSaved(true);
      else onClose();
    } catch (failure) {
      setError(failure);
    }
  });

  const addFiles = async (files: FileList | null) => {
    if (!current || !files) return;
    setError(null);
    const actions = pageActions(current.id);
    for (const file of Array.from(files)) {
      const problem = pageFileProblem(file);
      if (problem) {
        setError({ code: problem });
        continue;
      }
      setUpload({ name: file.name, percent: 0 });
      try {
        const page = await actions.upload(file, ({ sent, total }) =>
          setUpload({ name: file.name, percent: total ? Math.round((sent / total) * 100) : 0 }),
        );
        setPages((list) => [...list, page].sort((a, b) => a.sortOrder - b.sortOrder));
      } catch (failure) {
        setError(failure);
      }
    }
    setUpload(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const move = async (index: number, delta: -1 | 1) => {
    if (!current) return;
    const page = pages[index];
    const other = pages[index + delta];
    if (!page || !other) return;
    setError(null);
    try {
      const actions = pageActions(current.id);
      await actions.move(page, other.sortOrder);
      await actions.move(other, page.sortOrder);
      const next = [...pages];
      next[index] = { ...other, sortOrder: page.sortOrder, rowVersion: other.rowVersion + 1 };
      next[index + delta] = {
        ...page,
        sortOrder: other.sortOrder,
        rowVersion: page.rowVersion + 1,
      };
      setPages(next);
    } catch (failure) {
      setError(failure);
    }
  };

  const removePage = async (page: CertificatePage) => {
    if (!current) return;
    setError(null);
    try {
      await pageActions(current.id).remove(page);
      setPages((list) => list.filter((p) => p.id !== page.id));
    } catch (failure) {
      setError(failure);
    }
  };

  const date = (name: keyof CertificateValues & string, label: string, extra = {}) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <DayInput
          label={label}
          clearable
          value={field.value as string | null}
          onChange={field.onChange}
          error={errorOf(errors, name)}
          {...extra}
        />
      )}
    />
  );

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        position="right"
        size="lg"
        title={
          <span className={classes.drawerTitle}>{current ? k('editTitle') : k('addTitle')}</span>
        }
        closeButtonProps={{ 'aria-label': t('vesselCertificates.dialogs.close') }}
      >
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <Stack gap="lg">
            {saved ? (
              <Alert color="green" icon={<IconCheck size={16} />} role="status">
                {k('saved')}
              </Alert>
            ) : null}
            <Section title={k('sections.identity')}>
              <Controller
                control={form.control}
                name="typeId"
                render={({ field }) => (
                  <Select
                    label={k('type')}
                    required
                    searchable
                    data={sortedTypes.map((type) => ({ value: type.id, label: type.name }))}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      const type = types.find((x) => x.id === value);
                      if (type && form.getValues('name').trim() === '') {
                        form.setValue('name', `${type.name} Certificate`, { shouldDirty: true });
                      }
                    }}
                    error={errorOf(errors, 'typeId')}
                    comboboxProps={{ withinPortal: true }}
                  />
                )}
              />
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label={k('number')}
                  required
                  {...form.register('number')}
                  error={errorOf(errors, 'number')}
                />
                <TextInput
                  label={k('issuedBy')}
                  required
                  {...form.register('issuedBy')}
                  error={errorOf(errors, 'issuedBy')}
                />
              </SimpleGrid>
              <TextInput
                label={k('name')}
                required
                {...form.register('name')}
                error={errorOf(errors, 'name')}
              />
            </Section>
            <Section title={k('sections.dates')}>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {date('issueDate', k('issueDate'), {
                  required: true,
                  clearable: false,
                  maxDate: today,
                })}
                {date('expiryDate', k('expiryDate'))}
              </SimpleGrid>
              {date('anniversaryDate', k('anniversaryDate'), {
                description: k('anniversaryHint'),
              })}
            </Section>
            <Section title={k('sections.survey')}>
              <p className={classes.muted}>{k('overrideHint')}</p>
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                {date('nextSurveyOverride', k('nextSurveyOverride'))}
                {date('windowFromOverride', k('windowFromOverride'))}
                {date('windowToOverride', k('windowToOverride'))}
              </SimpleGrid>
              <Textarea
                label={k('overrideReason')}
                autosize
                minRows={2}
                {...form.register('overrideReason')}
                error={errorOf(errors, 'overrideReason')}
              />
            </Section>
            <Section title={k('sections.links')}>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Controller
                  control={form.control}
                  name="renewedFromId"
                  render={({ field }) => (
                    <Select
                      label={k('renewedFrom')}
                      searchable
                      data={[
                        { value: '', label: k('renewedNone') },
                        ...renewals.map((item) => ({
                          value: item.id,
                          label: `${item.name} · ${item.number}`,
                        })),
                      ]}
                      value={field.value ?? ''}
                      allowDeselect={false}
                      onChange={(value) => field.onChange(value ? value : null)}
                      error={errorOf(errors, 'renewedFromId')}
                      comboboxProps={{ withinPortal: true }}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="onBoardIssuanceId"
                  render={({ field }) => (
                    <Select
                      label={k('onBoard')}
                      data={[
                        { value: '', label: k('onBoardNone') },
                        ...linkable.map((i) => ({
                          value: i.id,
                          label: `${i.number} · ${i.issuedBy}`,
                        })),
                      ]}
                      value={field.value ?? ''}
                      allowDeselect={false}
                      onChange={(value) => field.onChange(value ? value : null)}
                      error={errorOf(errors, 'onBoardIssuanceId')}
                      comboboxProps={{ withinPortal: true }}
                    />
                  )}
                />
              </SimpleGrid>
            </Section>
            <Section title={k('sections.pages')}>
              {current ? (
                <>
                  <p className={classes.muted}>{k('pagesHint')}</p>
                  {pages.length > 0 ? (
                    <ul className={classes.files}>
                      {pages.map((page, index) => (
                        <li key={page.id} className={classes.pageRow}>
                          <IconFileTypePdf size={18} aria-hidden />
                          <span className={classes.pageRowName}>
                            {page.fileName}{' '}
                            <span className={classes.muted}>
                              {formatFileSize(page.size, i18n.resolvedLanguage ?? 'en')}
                            </span>
                          </span>
                          <span className={classes.rowActions}>
                            <Tooltip label={k('moveUp', { name: page.fileName })} withinPortal>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                disabled={index === 0}
                                aria-label={k('moveUp', { name: page.fileName })}
                                onClick={() => void move(index, -1)}
                              >
                                <IconArrowUp size={15} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={k('moveDown', { name: page.fileName })} withinPortal>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                disabled={index === pages.length - 1}
                                aria-label={k('moveDown', { name: page.fileName })}
                                onClick={() => void move(index, 1)}
                              >
                                <IconArrowDown size={15} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={k('removePage', { name: page.fileName })} withinPortal>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                aria-label={k('removePage', { name: page.fileName })}
                                onClick={() => void removePage(page)}
                              >
                                <IconTrash size={15} />
                              </ActionIcon>
                            </Tooltip>
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {upload ? (
                    <div>
                      <span className={classes.muted}>
                        {k('uploading', { name: upload.name, percent: upload.percent })}
                      </span>
                      <Progress value={upload.percent} size="sm" mt={4} aria-hidden />
                    </div>
                  ) : null}
                  <input
                    ref={fileInput}
                    type="file"
                    multiple
                    accept={SCAN_ACCEPT}
                    className={classes.fileInput}
                    aria-label={k('upload')}
                    tabIndex={-1}
                    onChange={(event) => void addFiles(event.currentTarget.files)}
                  />
                  <Button
                    variant="default"
                    leftSection={<IconUpload size={16} aria-hidden />}
                    loading={upload !== null}
                    onClick={() => fileInput.current?.click()}
                  >
                    {k('upload')}
                  </Button>
                </>
              ) : (
                <p className={classes.muted}>{k('pagesAfterSave')}</p>
              )}
            </Section>
          </Stack>
          {error ? (
            <p className={classes.formError} role="alert">
              {errorText(error)}
            </p>
          ) : null}
          <div className={classes.dialogActions}>
            {current && canRemove ? (
              <Button
                variant="subtle"
                color="red"
                mr="auto"
                leftSection={<IconTrash size={15} aria-hidden />}
                onClick={() => setConfirmRemove(true)}
              >
                {k('remove')}
              </Button>
            ) : null}
            <Button variant="default" onClick={onClose}>
              {t('vesselCertificates.dialogs.cancel')}
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {k('save')}
            </Button>
          </div>
        </form>
      </Drawer>
      <ConfirmRemove
        opened={confirmRemove}
        title={k('removeTitle')}
        body={k('removeBody', { name: current?.name ?? '' })}
        confirm={k('remove')}
        onClose={() => setConfirmRemove(false)}
        onConfirm={async () => {
          if (!current) return;
          await onRemove(current);
          setConfirmRemove(false);
          onClose();
        }}
      />
    </>
  );
}

/** Silme onayı (soft delete; geçmişte ve denetim kaydında kalır [NR496 4.3.2a]). */
export function ConfirmRemove({
  opened,
  title,
  body,
  confirm,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  title: string;
  body: string;
  confirm: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useCertificateErrorText();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (opened) setError(null);
  }, [opened]);
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span className={classes.drawerTitle}>{title}</span>}
      closeButtonProps={{ 'aria-label': t('vesselCertificates.dialogs.close') }}
    >
      <p>{body}</p>
      {error ? (
        <p className={classes.formError} role="alert">
          {errorText(error)}
        </p>
      ) : null}
      <div className={classes.dialogActions}>
        <Button variant="default" onClick={onClose}>
          {t('vesselCertificates.dialogs.cancel')}
        </Button>
        <Button
          color="red"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await onConfirm();
            } catch (failure) {
              setError(failure);
            } finally {
              setBusy(false);
            }
          }}
        >
          {confirm}
        </Button>
      </div>
    </Modal>
  );
}
